import { callRpc } from "./client";

export type StatusColega = "DISPONIVEL" | "AUSENTE" | "OCUPADO" | "OFFLINE";
export type TipoConversa = "INDIVIDUAL" | "GRUPO";

export interface ColegaChat {
  codLogin: number;
  nome: string;
  cargo: string;
  email: string;
  avatarId: string;
  status: StatusColega;
  /** Presença real (heartbeat), independente do status escolhido no perfil. */
  online: boolean;
  /** Login Master/Suporte da plataforma (fora da tabela usuarios). */
  suporte: boolean;
}

export interface ParticipanteChat {
  codLogin: number;
  nome: string;
  cargo: string;
  avatarId: string;
  status: StatusColega;
  online: boolean;
  papel: string;
  digitando: boolean;
  suporte: boolean;
}

export interface ConversaChat {
  codConversa: number;
  tipo: TipoConversa;
  nome: string;
  subtitulo: string;
  avatarId: string;
  status: StatusColega | "";
  codLoginContraparte: number;
  /** Conversa direta com um login Master/Suporte da plataforma. */
  suporte: boolean;
  ultimaMensagem: string;
  ultimaMensagemEm: string | null;
  totalMensagens: number;
  ultimaMensagemPropria: boolean;
  ultimaMensagemLida: boolean;
  naoLidas: number;
  arquivada: boolean;
  silenciada: boolean;
  digitando: boolean;
  participantes: ParticipanteChat[];
}

export interface MensagemChat {
  codMensagem: number;
  codLogin: number;
  propria: boolean;
  tipo: string;
  texto: string;
  anexoId: string;
  anexoNome: string;
  anexoTamanho: number;
  autorNome: string;
  autorAvatarId: string;
  criadaEm: string;
}

interface RespostaMensagens {
  sucesso?: boolean;
  erro?: string;
  mensagens?: MensagemChat[];
  lidoAteMensagem?: number;
  /** Até onde todos os outros participantes leram (marca o duplo check). */
  lidoPorTodosAte?: number;
}

interface RespostaSimples {
  sucesso?: boolean;
  erro?: string;
  codConversa?: number;
  codMensagem?: number;
}

export async function listarColegasChat(): Promise<ColegaChat[]> {
  const resposta = await callRpc<ColegaChat[]>("listarColegasChatInterno").catch(() => null);
  return Array.isArray(resposta) ? resposta : [];
}

export async function listarConversasChat(arquivadas = false): Promise<ConversaChat[]> {
  const resposta = await callRpc<ConversaChat[]>("listarConversasChatInterno", { arquivadas }).catch(
    () => null
  );
  return Array.isArray(resposta) ? resposta : [];
}

export type EstadoNaoLidasChat = {
  total: number;
  ultimaMensagemRecebidaId: number;
  ultimaConversaRecebidaId: number;
};

export async function obterEstadoNaoLidasChat(): Promise<EstadoNaoLidasChat> {
  const resposta = await callRpc<Partial<EstadoNaoLidasChat>>("contarNaoLidasChatInterno").catch(() => null);
  return {
    total: Number(resposta?.total ?? 0),
    ultimaMensagemRecebidaId: Number(resposta?.ultimaMensagemRecebidaId ?? 0),
    ultimaConversaRecebidaId: Number(resposta?.ultimaConversaRecebidaId ?? 0),
  };
}

export async function contarNaoLidasChat(): Promise<number> {
  return (await obterEstadoNaoLidasChat()).total;
}

export async function abrirConversaDiretaChat(codLoginDestino: number): Promise<RespostaSimples> {
  return callRpc<RespostaSimples>("abrirConversaDiretaChatInterno", { codLoginDestino });
}

export async function criarGrupoChat(dados: {
  nome: string;
  descricao?: string;
  participantes: number[];
  avatarBase64?: string;
}): Promise<RespostaSimples> {
  return callRpc<RespostaSimples>("criarGrupoChatInterno", dados);
}

export async function editarGrupoChat(dados: {
  codConversa: number;
  nome: string;
  participantes: number[];
  avatarBase64?: string;
  removerAvatar?: boolean;
}): Promise<RespostaSimples> {
  return callRpc<RespostaSimples>("editarGrupoChatInterno", dados);
}

export async function excluirGrupoChat(codConversa: number): Promise<RespostaSimples> {
  return callRpc<RespostaSimples>("excluirGrupoChatInterno", { codConversa });
}

export async function listarMensagensChat(
  codConversa: number,
  desdeCodMensagem = 0
): Promise<RespostaMensagens> {
  return callRpc<RespostaMensagens>("listarMensagensChatInterno", {
    codConversa,
    desdeCodMensagem,
  });
}

export async function enviarMensagemChat(dados: {
  codConversa: number;
  texto?: string;
  anexoBase64?: string;
  anexoNome?: string;
}): Promise<RespostaSimples> {
  return callRpc<RespostaSimples>("enviarMensagemChatInterno", dados);
}

export async function marcarConversaLidaChat(
  codConversa: number,
  codMensagem = 0
): Promise<RespostaSimples> {
  return callRpc<RespostaSimples>("marcarConversaLidaChatInterno", { codConversa, codMensagem });
}

export async function sinalizarDigitandoChat(codConversa: number): Promise<void> {
  await callRpc("sinalizarDigitandoChatInterno", { codConversa }).catch(() => null);
}

export async function arquivarConversaChat(
  codConversa: number,
  arquivada: boolean
): Promise<RespostaSimples> {
  return callRpc<RespostaSimples>("arquivarConversaChatInterno", { codConversa, arquivada });
}

/* ---------- Visão administrativa (Plataforma › Chat Interno) ---------- */

export interface ParticipanteConversaAdmin {
  codLogin: number;
  nome: string;
  cargo: string;
  avatarId: string;
  papel: string;
}

export interface ConversaAdminChat {
  codConversa: number;
  tipo: TipoConversa;
  titulo: string;
  descricao: string;
  organizacao: string;
  empresa: string;
  participantes: ParticipanteConversaAdmin[];
  totalParticipantes: number;
  totalMensagens: number;
  ultimaMensagem: string;
  ultimaMensagemEm: string | null;
  criadaEm: string | null;
}

export interface MensagemAdminChat {
  codMensagem: number;
  codLogin: number;
  tipo: string;
  texto: string;
  anexoId: string;
  anexoNome: string;
  anexoTamanho: number;
  autorNome: string;
  autorAvatarId: string;
  criadaEm: string;
}

export interface FiltrosConversasAdmin {
  busca?: string;
  tipo?: TipoConversa | "TODOS";
  dataInicio?: string;
  dataFim?: string;
  pagina?: number;
  porPagina?: number;
}

export async function listarConversasAdminChat(
  filtros: FiltrosConversasAdmin = {}
): Promise<{ registros: ConversaAdminChat[]; total: number; erro?: string }> {
  const resposta = await callRpc<{
    sucesso?: boolean;
    erro?: string;
    registros?: ConversaAdminChat[];
    total?: number;
  }>("listarConversasAdminChatInterno", filtros);
  if (resposta?.sucesso === false) {
    return { registros: [], total: 0, erro: resposta.erro || "Não foi possível carregar as conversas." };
  }
  return { registros: resposta?.registros ?? [], total: Number(resposta?.total ?? 0) };
}

export async function listarMensagensAdminChat(
  codConversa: number
): Promise<{ mensagens: MensagemAdminChat[]; erro?: string }> {
  const resposta = await callRpc<{
    sucesso?: boolean;
    erro?: string;
    mensagens?: MensagemAdminChat[];
  }>("listarMensagensAdminChatInterno", { codConversa });
  if (resposta?.sucesso === false) {
    return { mensagens: [], erro: resposta.erro || "Não foi possível carregar as mensagens." };
  }
  return { mensagens: resposta?.mensagens ?? [] };
}
